/**
 * migrate-ide command - Migrate .jai1 content to IDE directories
 */
import { Command } from 'commander';
import { checkbox, confirm } from '@inquirer/prompts';
import type { MigrateIDE, MigrateContentType } from '../types/migrate-ide.types.js';
import { MigrateIdeService } from '../services/migrate-ide.service.js';
import { IDE_MIGRATION_CONFIGS, getMigrationIDEs } from '../constants/ide-migration-configs.js';
import { checkboxTheme } from '../utils/prompt-theme.js';

/**
 * Create migrate-ide command
 */
export function createMigrateIdeCommand(): Command {
    const cmd = new Command('migrate-ide')
        .description('Migrate .jai1 rules và workflows sang IDEs (Cursor, Windsurf, Claude Code, Codex, etc.)')
        .option('--ide <ides...>', 'Target IDEs (cursor, windsurf, antigravity, claude, opencode, codex)')
        .option('--type <types...>', 'Content types (rules, workflows, commands, skills)')
        .option('--dry-run', 'Preview changes without writing files')
        .action(async (options: {
            ide?: string[];
            type?: string[];
            dryRun?: boolean;
        }) => {
            await runMigrateIde(options);
        });

    return cmd;
}

async function runMigrateIde(options: {
    ide?: string[];
    type?: string[];
    dryRun?: boolean;
}) {
    const service = new MigrateIdeService();

    console.log('\n🔄 Migrate rules và workflows sang IDE(s)\n');

    // Scan .jai1 content
    console.log('📁 Scanning .jai1/ directory...');
    const content = await service.scanJai1Content();

    if (content.totalCount === 0) {
        console.log('\n⚠️  Không tìm thấy content trong .jai1/');
        console.log('   💡 Hãy tạo files trong .jai1/rules/ hoặc .jai1/workflows/ trước.\n');
        process.exit(1);
    }

    console.log(`   Found: ${content.rules.length} rules, ${content.workflows.length} workflows\n`);

    // IDE alias map for backward compatibility
    const ideAliases: Record<string, MigrateIDE> = {
        'claudecode': 'claude',
        'codexcli': 'codex',
        'openai-codex': 'codex',
    };
    const normalizeIde = (ide: string): MigrateIDE => (ideAliases[ide] ?? ide) as MigrateIDE;

    // Select IDEs
    let selectedIdes: MigrateIDE[];
    if (options.ide && options.ide.length > 0) {
        selectedIdes = options.ide.map(normalizeIde);
    } else {
        const ideChoices = getMigrationIDEs().map(ide => {
            const config = IDE_MIGRATION_CONFIGS[ide];
            return {
                name: `${config.icon} ${config.name}`,
                value: ide,
            };
        });

        selectedIdes = await checkbox({
            message: 'Chọn IDE(s) để migrate (SPACE để chọn, ENTER để xác nhận):',
            choices: ideChoices,
            theme: checkboxTheme,
        });

        if (selectedIdes.length === 0) {
            console.log('\n⚠️  Bạn chưa chọn IDE nào!');
            console.log('   💡 Hãy chọn ít nhất 1 IDE.\n');
            process.exit(0);
        }
    }

    // Select content types
    let selectedTypes: MigrateContentType[];
    if (options.type && options.type.length > 0) {
        selectedTypes = options.type as MigrateContentType[];
    } else {
        const typeChoices = [
            { name: `Rules (${content.rules.length} files)`, value: 'rules' as MigrateContentType },
            { name: `Workflows (${content.workflows.length} files)`, value: 'workflows' as MigrateContentType },
            { name: `Commands (${content.commands.length} files)`, value: 'commands' as MigrateContentType },
        ];

        selectedTypes = await checkbox({
            message: 'Chọn content types để migrate:',
            choices: typeChoices,
            theme: checkboxTheme,
        });

        if (selectedTypes.length === 0) {
            console.log('\n⚠️  Bạn chưa chọn content type nào!');
            console.log('   💡 Hãy chọn ít nhất 1 content type.\n');
            process.exit(0);
        }
    }

    // Calculate total files
    const totalItems = selectedTypes.reduce((sum, type) => {
        return sum + (type === 'rules' ? content.rules.length :
                     type === 'workflows' ? content.workflows.length :
                     content.commands.length);
    }, 0);
    const totalFiles = totalItems * selectedIdes.length;

    // Preview
    console.log('\n📊 Preview:\n');
    console.log(`   IDEs: ${selectedIdes.map(ide => IDE_MIGRATION_CONFIGS[ide].name).join(', ')}`);
    console.log(`   Content types: ${selectedTypes.join(', ')}`);
    console.log(`   Will create/update: ${totalFiles} stub files\n`);

    if (options.dryRun) {
        console.log('🔍 DRY RUN - No files will be written\n');
    }

    // Confirm
    const confirmed = await confirm({
        message: 'Proceed with migration?',
        default: true,
    });

    if (!confirmed) {
        console.log('\n❌ Migration cancelled.\n');
        process.exit(0);
    }

    // Migrate
    console.log('\n🔄 Migrating...\n');
    let completed = 0;

    const results = await service.migrate(
        selectedIdes,
        selectedTypes,
        content,
        (result) => {
            completed++;
            const icon = result.status === 'created' ? '✓' :
                        result.status === 'updated' ? '↻' :
                        result.status === 'error' ? '✗' : '○';

            const percentage = Math.round((completed / totalFiles) * 100);
            console.log(`   ${icon} [${percentage}%] ${result.targetPath}`);

            if (result.status === 'error' && result.error) {
                console.log(`      Error: ${result.error}`);
            }
        }
    );

    // Summary
    const created = results.filter(r => r.status === 'created').length;
    const updated = results.filter(r => r.status === 'updated').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    const errors = results.filter(r => r.status === 'error').length;

    console.log('\n✅ Migration complete!\n');
    console.log(`   Created: ${created}`);
    console.log(`   Updated: ${updated}`);
    if (skipped > 0) console.log(`   Skipped: ${skipped}`);
    if (errors > 0) console.log(`   Errors: ${errors}`);
    console.log('');
}
